import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, MoreVertical, Copy, Trash2, Send, Plus, Pencil, Loader2,
  FileText, Receipt, CheckCircle2, XCircle, FileDown, Wrench, RefreshCw, Repeat, Sliders, Eye,
  ShieldCheck, LayoutList, Tags,
} from "lucide-react";
import { quotesApi, invoicesApi, engineersApi, maintenanceApi, companiesApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, fmtDate, apiError, statusTone, prettyStatus } from "@/lib/format";
import { groupQuoteItems, lineTotal } from "@/lib/quoteGroups";
import { downloadQuotePdf } from "@/pdf/webReports";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import MarketplaceQuoteCard from "@/components/MarketplaceQuoteCard";

function LineRow({ it, editable, onEdit, onDelete, showPrice = true, split = null }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2" data-testid={`quote-line-${it._index}`}>
      <div className="min-w-0">
        <p className="font-medium text-slate-800">{it.component || "Line item"}{it.quantity > 1 ? ` ×${it.quantity}` : ""}</p>
        {it.description && <p className="text-sm text-slate-500">{it.description}</p>}
        {showPrice && split && (
          <p className="mt-0.5 text-xs text-slate-400" data-testid={`quote-line-split-${it._index}`}>
            Labour {fmtMoney(split.labour)} · Materials {fmtMoney(split.materials)}
          </p>
        )}
        {Number(it.cost || 0) <= 0 && <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">Awaiting price</span>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {showPrice && <span className="font-semibold text-slate-800">{fmtMoney(lineTotal(it))}</span>}
        {editable && (
          <>
            <button className="text-slate-400 hover:text-slate-700" onClick={() => onEdit(it)} data-testid={`quote-line-edit-${it._index}`}><Pencil className="h-4 w-4" /></button>
            <button className="text-slate-400 hover:text-red-600" onClick={() => onDelete(it)} data-testid={`quote-line-delete-${it._index}`}><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>
    </div>
  );
}

function LineItemDialog({ open, onOpenChange, initial, onSave, saving }) {
  const [form, setForm] = useState({ component: "", description: "", cost: "", quantity: "1" });
  const [applyAll, setApplyAll] = useState(true);
  React.useEffect(() => {
    if (open) {
      setForm({
        component: initial?.component || "",
        description: initial?.description || "",
        cost: initial?.cost != null ? String(initial.cost) : "",
        quantity: initial?.quantity != null ? String(initial.quantity) : "1",
      });
      setApplyAll(true);
    }
  }, [open, initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.component.trim()) { toast.error("Enter a line name."); return; }
    onSave({
      component: form.component.trim(),
      description: form.description.trim(),
      cost: Number(form.cost || 0),
      quantity: Math.max(1, parseInt(form.quantity || "1", 10)),
      _applyAll: !!initial && applyAll,
      _failure_key: initial?.failure_key || null,
      _defect_type: initial?.defect_type || null,
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit line" : "Add line"}</DialogTitle>
          <DialogDescription>Line items make up the quote total.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>Item *</Label><Input value={form.component} onChange={set("component")} placeholder="e.g. Replace hinges" data-testid="line-component-input" /></div>
          <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={set("description")} placeholder="Optional detail" rows={2} data-testid="line-description-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Unit cost (£)</Label><Input type="number" step="0.01" min="0" value={form.cost} onChange={set("cost")} data-testid="line-cost-input" /></div>
            <div className="space-y-2"><Label>Quantity</Label><Input type="number" min="1" value={form.quantity} onChange={set("quantity")} data-testid="line-qty-input" /></div>
          </div>
          {initial && (
            <button
              type="button"
              onClick={() => setApplyAll((v) => !v)}
              data-testid="line-apply-all"
              className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${applyAll ? "border-primary bg-primary/5" : "border-slate-200"}`}
            >
              <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${applyAll ? "border-primary bg-primary text-white" : "border-slate-300"}`}>{applyAll && <CheckCircle2 className="h-3.5 w-3.5" />}</span>
              <span className="text-slate-600">Apply this price to every matching item in this quote &amp; save it to the price list for future quotes.</span>
            </button>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="line-save-btn">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save line</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DiscountDialog({ open, onOpenChange, initial, onSave, saving }) {
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");
  React.useEffect(() => {
    if (open) { setAmount(initial?.amount != null ? String(initial.amount) : ""); setLabel(initial?.label || "Goodwill discount"); }
  }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Discount</DialogTitle><DialogDescription>Applied to the subtotal before VAT.</DialogDescription></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ discount_amount: Number(amount || 0), discount_label: label.trim() || "Discount" }); }} className="space-y-4">
          <div className="space-y-2"><Label>Amount (£)</Label><Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="discount-amount-input" /></div>
          <div className="space-y-2"><Label>Label</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="discount-label-input" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="discount-save-btn">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save discount</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const DEPOSIT_OPTS = [
  { label: "Full invoice", body: {} },
  { label: "25% deposit", body: { percent: 25 } },
  { label: "50% deposit", body: { percent: 50 } },
  { label: "75% deposit", body: { percent: 75 } },
  { label: "Balance on completion", body: { balance: true } },
];

const NEW_DOOR_STYLES = [
  "FD30S Single", "FD30S Double", "FD60S Single", "FD60S Double",
  "Glazed FD30S", "Glazed FD60S", "custom",
];

const ExtraToggle = ({ label, checked, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition-colors ${checked ? "border-primary bg-primary/5 text-slate-800" : "border-slate-200 text-slate-600"}`}
  >
    <span>{label}</span>
    <span className={`flex h-5 w-5 items-center justify-center rounded border ${checked ? "border-primary bg-primary text-white" : "border-slate-300"}`}>{checked && <CheckCircle2 className="h-4 w-4" />}</span>
  </button>
);

function ReplaceDoorDialog({ open, onOpenChange, doors, preselect, onConfirm, saving }) {
  const [doorId, setDoorId] = useState("");
  const [style, setStyle] = useState("FD30S Single");
  const [customStyle, setCustomStyle] = useState("");
  const [frame, setFrame] = useState(false);
  const [architrave, setArchitrave] = useState(false);
  const [painting, setPainting] = useState(false);

  React.useEffect(() => {
    if (open) {
      setDoorId(preselect || (doors.length === 1 ? doors[0].id : ""));
      setStyle("FD30S Single"); setCustomStyle(""); setFrame(false); setArchitrave(false); setPainting(false);
    }
  }, [open, preselect, doors]);

  const canSubmit = !!doorId && (style !== "custom" || customStyle.trim().length >= 3);
  const submit = () => {
    onConfirm({
      door_id: doorId,
      style,
      custom_style: style === "custom" ? customStyle.trim() : undefined,
      include_frame: frame,
      include_architrave: architrave,
      include_painting: painting,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Replace with new door</DialogTitle>
          <DialogDescription>Swaps all failed items on a door for a single BS 8214-compliant new-door bundle. Originals are kept as an audit appendix.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {doors.length > 1 && (
            <div className="space-y-1.5">
              <Label>Which door?</Label>
              <Select value={doorId} onValueChange={setDoorId}>
                <SelectTrigger data-testid="replace-door-select"><SelectValue placeholder="Select a door" /></SelectTrigger>
                <SelectContent>
                  {doors.map((d) => <SelectItem key={d.id} value={d.id}>{d.door_reference || d.location || "Door"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Door style</Label>
            <div className="flex flex-wrap gap-2" data-testid="replace-door-styles">
              {NEW_DOOR_STYLES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStyle(s)}
                  data-testid={`replace-style-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${style === s ? "border-primary bg-primary text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                >
                  {s === "custom" ? "+ Custom" : s}
                </button>
              ))}
            </div>
          </div>
          {style === "custom" && (
            <div className="space-y-1.5">
              <Label>Custom style description</Label>
              <Input value={customStyle} onChange={(e) => setCustomStyle(e.target.value)} placeholder="e.g. FD90S vision panel + louvre" data-testid="replace-custom-style" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Include extras</Label>
            <div className="grid grid-cols-1 gap-2">
              <ExtraToggle label="New frame / lining" checked={frame} onChange={setFrame} testId="replace-frame" />
              <ExtraToggle label="New architrave" checked={architrave} onChange={setArchitrave} testId="replace-architrave" />
              <ExtraToggle label="Decoration / painting" checked={painting} onChange={setPainting} testId="replace-painting" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || !canSubmit} data-testid="replace-door-confirm">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Repeat className="mr-2 h-4 w-4" />} Replace door
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailsDialog({ open, onOpenChange, initial, onSave, saving }) {
  const [form, setForm] = useState({ customer_name: "", customer_email: "", valid_until: "", payment_terms_days: "" });
  React.useEffect(() => {
    if (open) {
      setForm({
        customer_name: initial?.customer_name || "",
        customer_email: initial?.customer_email || "",
        valid_until: initial?.valid_until ? String(initial.valid_until).slice(0, 10) : "",
        payment_terms_days: initial?.payment_terms_days != null ? String(initial.payment_terms_days) : "",
      });
    }
  }, [open, initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit quote details</DialogTitle>
          <DialogDescription>Customer, validity and payment terms shown on the quote.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave({
              customer_name: form.customer_name.trim() || null,
              customer_email: form.customer_email.trim() || null,
              valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
              payment_terms_days: form.payment_terms_days === "" ? null : Math.max(0, parseInt(form.payment_terms_days, 10) || 0),
            });
          }}
          className="space-y-4"
        >
          <div className="space-y-2"><Label>Customer name</Label><Input value={form.customer_name} onChange={set("customer_name")} data-testid="details-name-input" /></div>
          <div className="space-y-2"><Label>Customer email</Label><Input type="email" value={form.customer_email} onChange={set("customer_email")} data-testid="details-email-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Valid until</Label><Input type="date" value={form.valid_until} onChange={set("valid_until")} data-testid="details-valid-input" /></div>
            <div className="space-y-2"><Label>Payment terms (days)</Label><Input type="number" min="0" value={form.payment_terms_days} onChange={set("payment_terms_days")} data-testid="details-terms-input" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="details-save-btn">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save details</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CustomisePdfDialog({ open, onOpenChange, quoteId, company, onError }) {
  const [coverLetter, setCoverLetter] = useState("");
  const [terms, setTerms] = useState("");
  const [showDoorPhotos, setShowDoorPhotos] = useState(true);
  const [photoAppendix, setPhotoAppendix] = useState(false);
  const [saveDefault, setSaveDefault] = useState(false);
  const [busy, setBusy] = useState(null); // 'preview' | 'download' | null

  React.useEffect(() => {
    if (open) {
      setCoverLetter(company?.quote_intro_wording || "");
      setTerms(company?.quote_terms || "");
      setShowDoorPhotos(company?.quote_show_door_photos !== false);
      setPhotoAppendix(false);
      setSaveDefault(false);
    }
  }, [open, company]);

  const run = async (download) => {
    setBusy(download ? "download" : "preview");
    try {
      if (saveDefault) {
        try { await companiesApi.update({ quote_intro_wording: coverLetter, quote_terms: terms }); }
        catch (e) { onError(apiError(e, "Could not save company defaults")); }
      }
      await downloadQuotePdf(quoteId, { coverLetter, terms, showDoorPhotos, photoAppendix, download });
      toast.success(download ? "Quote PDF downloaded" : "Preview opened in a new tab");
    } catch (e) {
      onError(apiError(e, "Could not generate PDF"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customise &amp; preview PDF</DialogTitle>
          <DialogDescription>Tweak the cover letter, terms and photos for this quote, then preview before sending.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Cover letter</Label>
            <Textarea rows={4} value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} placeholder="Intro paragraph shown at the top of the quote…" data-testid="pdf-cover-input" />
          </div>
          <div className="space-y-2">
            <Label>Terms &amp; conditions</Label>
            <Textarea rows={4} value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Payment terms, validity, exclusions…" data-testid="pdf-terms-input" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-100 p-3">
            <div><p className="text-sm font-medium text-slate-700">Show door photos</p><p className="text-xs text-slate-400">Inline photos next to each door.</p></div>
            <Switch checked={showDoorPhotos} onCheckedChange={setShowDoorPhotos} data-testid="pdf-door-photos-toggle" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-100 p-3">
            <div><p className="text-sm font-medium text-slate-700">Photo appendix</p><p className="text-xs text-slate-400">All photos grouped at the end.</p></div>
            <Switch checked={photoAppendix} onCheckedChange={setPhotoAppendix} data-testid="pdf-appendix-toggle" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} data-testid="pdf-save-default" />
            Save cover letter &amp; terms as company default
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={!!busy} onClick={() => run(false)} data-testid="pdf-preview-btn">
            {busy === "preview" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Preview
          </Button>
          <Button disabled={!!busy} onClick={() => run(true)} data-testid="pdf-download-btn">
            {busy === "download" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />} Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: quote, loading, error, reload } = useAsync(() => quotesApi.getOne(id), [id]);
  const invoices = useAsync(() => invoicesApi.list({ quote_id: id }), [id]);
  const company = useAsync(() => companiesApi.me(), []);

  const [busy, setBusy] = useState(false);
  const [lineDialog, setLineDialog] = useState({ open: false, initial: null });
  const [discountOpen, setDiscountOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [jobOpen, setJobOpen] = useState(false);
  const [jobEngineer, setJobEngineer] = useState("");
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [customiseOpen, setCustomiseOpen] = useState(false);
  const [reinspectOpen, setReinspectOpen] = useState(false);
  const [reinspectPrice, setReinspectPrice] = useState("");
  const [reinspectWording, setReinspectWording] = useState("");
  const engineers = useAsync(() => engineersApi.list(), []);

  React.useEffect(() => { setBusy(false); }, [id]);

  if (loading) return <div><BackLink /><LoadingState label="Loading quote…" /></div>;
  if (error) return <div><BackLink /><ErrorState message={error} onRetry={reload} /></div>;
  if (!quote) return null;

  const editable = quote.status === "draft";
  const grouped = groupQuoteItems(quote.items || []);
  const invoiceList = Array.isArray(invoices.data) ? invoices.data : [];
  const invoicedTotal = invoiceList.reduce((s, x) => s + Number(x.total || 0), 0);

  // Labour vs Materials presentation — mirrors the mobile quote screen +
  // the web PDF (webReports.js). Effective mode resolves per-quote →
  // company default → inspection/maintenance fallback.
  const companyTpl = company.data || {};
  const isInspectionQuote = (quote.quote_type || "maintenance") === "inspection";
  const effectiveMode = quote.display_mode || companyTpl.quote_display_mode || (isInspectionQuote ? "total_only" : "labour_materials");
  const showLabourMaterials = effectiveMode === "labour_materials";
  const labourRatio = typeof companyTpl.quote_default_labour_ratio === "number" ? companyTpl.quote_default_labour_ratio : 0.6;
  const splitLine = (it) => {
    const cost = Number(it.cost || 0);
    const el = it.labour_cost;
    const em = it.materials_cost;
    if (typeof el === "number" || typeof em === "number") {
      if (typeof el === "number" && typeof em !== "number") return { labour: el, materials: Math.max(0, cost - el) };
      if (typeof em === "number" && typeof el !== "number") return { labour: Math.max(0, cost - em), materials: em };
      return { labour: Number(el || 0), materials: Number(em || 0) };
    }
    const labour = +(cost * labourRatio).toFixed(2);
    return { labour, materials: +(cost - labour).toFixed(2) };
  };
  const lineSplit = (it) => {
    const qty = Number(it.quantity || 1);
    const s = splitLine(it);
    return { labour: s.labour * qty, materials: s.materials * qty };
  };
  const { totalLabour, totalMaterials } = (quote.items || []).reduce((acc, it) => {
    const s = lineSplit(it);
    acc.totalLabour += s.labour;
    acc.totalMaterials += s.materials;
    return acc;
  }, { totalLabour: 0, totalMaterials: 0 });

  const saveDisplayMode = async (mode) => {
    setBusy(true);
    try { await quotesApi.update(id, { display_mode: mode }); await reload(); }
    catch (e) { toast.error(apiError(e, "Could not change layout")); }
    finally { setBusy(false); }
  };

  const saveDetails = async (patch) => {
    setBusy(true);
    try { await quotesApi.update(id, patch); await reload(); setDetailsOpen(false); toast.success("Details updated"); }
    catch (e) { toast.error(apiError(e, "Could not save details")); }
    finally { setBusy(false); }
  };

  const saveItems = async (items) => {
    setBusy(true);
    try { await quotesApi.update(id, { items }); await reload(); }
    catch (e) { toast.error(apiError(e, "Could not save")); }
    finally { setBusy(false); }
  };

  const addOrEditLine = async (payload) => {
    const { _applyAll, _failure_key, _defect_type, ...lineData } = payload;
    const items = [...(quote.items || [])];
    if (lineDialog.initial) {
      const idx = lineDialog.initial._index;
      items[idx] = { ...items[idx], ...lineData };
    } else {
      items.push({ ...lineData, extra_work: true });
    }
    await saveItems(items);
    setLineDialog({ open: false, initial: null });
    // Cascade the price across matching lines in this quote + save to the
    // price list, so re-used failures are priced consistently everywhere.
    // Allow a deliberate £0 (no-charge / included) to cascade too — the
    // backend accepts any cost >= 0.
    if (_applyAll && Number.isFinite(Number(lineData.cost)) && Number(lineData.cost) >= 0) {
      try {
        const res = await quotesApi.applyPrice(id, {
          component: lineData.component,
          failure_key: _failure_key || undefined,
          defect_type: _defect_type || undefined,
          cost: Number(lineData.cost),
          save_to_library: true,
        });
        const { matched, cascaded_quotes } = res.data || {};
        if (matched > 1 || cascaded_quotes > 0) {
          toast.success(`Priced ${matched} matching line${matched === 1 ? "" : "s"}${cascaded_quotes ? ` · updated ${cascaded_quotes} other draft quote${cascaded_quotes === 1 ? "" : "s"}` : ""} · saved to price list`);
        } else {
          toast.success("Saved to price list");
        }
        await reload();
      } catch (e) { toast.error(apiError(e, "Could not apply price")); }
    }
  };

  const deleteLine = async (it) => {
    const items = (quote.items || []).filter((_, i) => i !== it._index);
    await saveItems(items);
  };

  const saveDiscount = async ({ discount_amount, discount_label }) => {
    setBusy(true);
    try { await quotesApi.update(id, { discount_amount, discount_label }); await reload(); setDiscountOpen(false); }
    catch (e) { toast.error(apiError(e, "Could not save discount")); }
    finally { setBusy(false); }
  };

  const saveNotes = async () => {
    setBusy(true);
    try { await quotesApi.update(id, { notes: notesDraft }); await reload(); setNotesOpen(false); }
    catch (e) { toast.error(apiError(e, "Could not save notes")); }
    finally { setBusy(false); }
  };

  const doSend = async (allowUnpriced = false) => {
    setBusy(true);
    try {
      await quotesApi.send(id, allowUnpriced ? { allow_unpriced: true } : {});
      toast.success("Quote sent to the client portal");
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "unpriced_lines") {
        setBusy(false);
        if (window.confirm(`${d.message} Send anyway?`)) return doSend(true);
        return;
      }
      toast.error(apiError(e, "Could not send quote"));
    } finally { setBusy(false); }
  };

  const doDecision = async (decision) => {
    setBusy(true);
    try {
      await quotesApi.decision(id, { decision, signature_name: "Recorded by staff (web)" });
      toast.success(`Quote ${decision}`);
      await reload();
    } catch (e) { toast.error(apiError(e, "Could not record decision")); }
    finally { setBusy(false); }
  };

  const doDownloadPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadQuotePdf(id);
      toast.success("Quote PDF generated");
    } catch (e) {
      toast.error(apiError(e, "Could not generate PDF"));
    } finally {
      setPdfBusy(false);
    }
  };

  const doDuplicate = async () => {
    setBusy(true);
    try { const res = await quotesApi.duplicate(id); toast.success("Quote duplicated"); navigate(`/quotes/${res.data.id}`); }
    catch (e) { toast.error(apiError(e, "Could not duplicate")); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    setBusy(true);
    try { await quotesApi.remove(id); toast.success("Quote deleted"); navigate("/quotes"); }
    catch (e) { toast.error(apiError(e, "Could not delete")); }
    finally { setBusy(false); }
  };

  const createInvoice = async (body) => {
    setBusy(true);
    try {
      const res = await quotesApi.generateInvoice(id, body);
      toast.success("Invoice created");
      setInvoiceOpen(false);
      navigate(`/invoices/${res.data.id}`);
    } catch (e) { toast.error(apiError(e, "Could not create invoice")); }
    finally { setBusy(false); }
  };

  const createJob = async () => {
    setBusy(true);
    try {
      await maintenanceApi.create({ quote_id: id, engineer_id: jobEngineer || undefined, split_by_door: true });
      toast.success("Maintenance job created");
      setJobOpen(false);
      navigate("/maintenance");
    } catch (e) { toast.error(apiError(e, "Could not create job")); }
    finally { setBusy(false); }
  };

  const isMaintenanceQuote = (quote.quote_type || "maintenance") !== "inspection";
  const engList = Array.isArray(engineers.data) ? engineers.data : [];

  const doRegenerate = async () => {
    setBusy(true);
    try {
      const res = await quotesApi.regenerate(id);
      toast.success(`Quote rebuilt — ${res.data.items_added} line(s)`);
      setRegenOpen(false);
      await reload();
    } catch (e) { toast.error(apiError(e, "Could not regenerate")); }
    finally { setBusy(false); }
  };

  const doConvertDoor = async (payload) => {
    setBusy(true);
    try {
      await quotesApi.convertToNewDoor(id, payload);
      toast.success("Door replaced with a new-door bundle");
      setReplaceOpen(false);
      await reload();
    } catch (e) { toast.error(apiError(e, "Could not replace door")); }
    finally { setBusy(false); }
  };

  const regenerable = editable && !!quote.inspection_id;
  const replaceableDoors = (() => {
    const seen = new Map();
    (quote.items || []).forEach((it) => {
      if (it.door_id && it.bundle_tag !== "new_door" && !seen.has(it.door_id)) {
        seen.set(it.door_id, { id: it.door_id, door_reference: it.door_reference, location: it.door_location });
      }
    });
    return Array.from(seen.values());
  })();
  const canReplaceDoor = editable && replaceableDoors.length > 0;

  const unpricedCount = (quote.items || []).filter((it) => (Number(it.cost) || 0) <= 0).length;

  // Post-work inspection & compliance report add-on (one line per door).
  const reinspectionLines = (quote.items || []).filter((it) => it?.bundle_tag === "reinspection");
  const reinspectionOn = reinspectionLines.length > 0;
  const reinspectionPrice = typeof companyTpl.quote_reinspection_price === "number" ? companyTpl.quote_reinspection_price : 37.5;
  const reinspectionWording = companyTpl.quote_reinspection_wording || "Fire door inspection and compliance report issued upon completion";
  const reinspDoorCount = Math.max(1, grouped.doorGroups.length);
  const reinspLineTotal = reinspectionLines.reduce((a, it) => a + (Number(it.cost) || 0) * (Number(it.quantity) || 1), 0);
  const reinspectionSummary = reinspectionOn
    ? `"${reinspectionLines[0]?.component || reinspectionWording}" · ${fmtMoney(reinspectionLines[0]?.cost ?? reinspectionPrice)} per door — ${reinspectionLines.length} door${reinspectionLines.length === 1 ? "" : "s"} · ${fmtMoney(reinspLineTotal)} ex VAT`
    : `Add "${reinspectionWording}" · ${fmtMoney(reinspectionPrice)} per door (${reinspDoorCount} door${reinspDoorCount === 1 ? "" : "s"} → ${fmtMoney(reinspectionPrice * reinspDoorCount)})`;

  const toggleReinspection = async (enabled, opts) => {
    setBusy(true);
    try { await quotesApi.setReinspection(id, enabled, opts || {}); await reload(); return true; }
    catch (e) { toast.error(apiError(e, "Could not update quote")); return false; }
    finally { setBusy(false); }
  };
  const openReinspectEditor = () => {
    const cur = reinspectionLines[0];
    setReinspectPrice(Number(cur?.cost ?? reinspectionPrice).toFixed(2));
    setReinspectWording(String(cur?.component || reinspectionWording));
    setReinspectOpen(true);
  };
  const saveReinspectEditor = async () => {
    const price = parseFloat(String(reinspectPrice).replace(/[£,\s]/g, ""));
    if (!Number.isFinite(price) || price < 0 || price > 10000) { toast.error("Enter a price per door between £0 and £10,000."); return; }
    const wording = reinspectWording.trim();
    if (!wording) { toast.error("Enter the text the client should see for this line."); return; }
    const ok = await toggleReinspection(true, { price, wording });
    if (ok) setReinspectOpen(false);
  };

  return (
    <div>
      <BackLink />
      <PageHeader
        title={quote.title || "Quote"}
        subtitle={[quote.building_name, quote.customer_name].filter(Boolean).join(" · ")}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={statusTone(quote.status)}>{prettyStatus(quote.status)}</Badge>
            <Button variant="outline" size="sm" onClick={() => navigate(`/reports/build?mode=quote&quoteId=${id}`)} data-testid="quote-edit-send">
              <FileText className="mr-1.5 h-4 w-4" /> Edit &amp; send
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCustomiseOpen(true)} data-testid="quote-customise-pdf">
              <Sliders className="mr-1.5 h-4 w-4" /> Customise PDF
            </Button>
            <Button variant="outline" size="sm" disabled={pdfBusy} onClick={doDownloadPdf} data-testid="quote-download-pdf">
              {pdfBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} PDF
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="icon" data-testid="quote-actions-trigger"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={doDuplicate} data-testid="quote-duplicate"><Copy className="mr-2 h-4 w-4" /> Duplicate</DropdownMenuItem>
                <DropdownMenuItem onClick={doDelete} className="text-red-600 focus:text-red-600" data-testid="quote-delete"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
        testId="quote-detail-header"
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Line items */}
          <Card className="p-5">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-semibold text-slate-800"><FileText className="h-5 w-5 text-primary" /> Works</h3>
              {editable && <Button size="sm" variant="outline" onClick={() => setLineDialog({ open: true, initial: null })} data-testid="quote-add-line"><Plus className="mr-1.5 h-4 w-4" /> Add line</Button>}
            </div>
            {editable && (
              <div className="mb-3" data-testid="quote-display-mode">
                <p className="mb-1 text-xs font-medium text-slate-500">Client pricing layout</p>
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
                  {[
                    { v: "labour_materials", label: "Labour & materials" },
                    { v: "total_only", label: "Totalled per door" },
                    { v: "inherit", label: "Company default" },
                  ].map((opt) => {
                    const active = (quote.display_mode || "inherit") === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        disabled={busy}
                        onClick={() => saveDisplayMode(opt.v)}
                        data-testid={`quote-mode-${opt.v}`}
                        className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {!quote.display_mode && (
                  <p className="mt-1 text-xs text-slate-400">
                    Using company default → {showLabourMaterials ? "Labour & materials shown separately" : "one total per door"}.
                  </p>
                )}
              </div>
            )}
            {editable && (quote.items || []).length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2" data-testid="quote-print-tools">
                <Button size="sm" variant="outline" onClick={() => navigate(`/quotes/${id}/full-edit`)} data-testid="quote-open-full-edit">
                  <LayoutList className="mr-1.5 h-4 w-4" /> Edit in print layout
                </Button>
                {unpricedCount > 0 && (
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => navigate(`/quotes/${id}/full-edit?bulk=1`)} data-testid="quote-set-all-prices">
                    <Tags className="mr-1.5 h-4 w-4" /> Set all prices ({unpricedCount})
                  </Button>
                )}
              </div>
            )}
            {grouped.doorGroups.length === 0 && grouped.additional.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-400">No line items yet.</p>
            )}
            {grouped.doorGroups.map((g) => (
              <div key={g.key} className="border-t border-slate-100 py-3 first:border-t-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">{g.door_reference || "Door"}{g.door_location ? ` · ${g.door_location}` : ""}</p>
                  <span className="text-sm font-semibold text-slate-800" data-testid={`quote-door-total-${g.key}`}>{fmtMoney(g.total)}</span>
                </div>
                <div className="mt-1 divide-y divide-slate-50">
                  {g.items.map((it) => (
                    <LineRow
                      key={it._index}
                      it={it}
                      editable={editable}
                      showPrice={showLabourMaterials}
                      split={showLabourMaterials ? lineSplit(it) : null}
                      onEdit={(x) => setLineDialog({ open: true, initial: x })}
                      onDelete={deleteLine}
                    />
                  ))}
                </div>
              </div>
            ))}
            {grouped.additional.length > 0 && (
              <div className="border-t border-slate-100 py-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">Additional works</p>
                  <span className="text-sm font-semibold text-slate-800" data-testid="quote-additional-total">{fmtMoney(grouped.additionalTotal)}</span>
                </div>
                <div className="mt-1 divide-y divide-slate-50">
                  {grouped.additional.map((it) => (
                    <LineRow
                      key={it._index}
                      it={it}
                      editable={editable}
                      showPrice={showLabourMaterials}
                      split={showLabourMaterials ? lineSplit(it) : null}
                      onEdit={(x) => setLineDialog({ open: true, initial: x })}
                      onDelete={deleteLine}
                    />
                  ))}
                </div>
              </div>
            )}
          </Card>

          {(editable || reinspectionOn) && (
            <Card className="p-5" data-testid="quote-reinspection-card">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-slate-800">Post-work inspection &amp; compliance report</h3>
                  <p className="mt-0.5 text-sm text-slate-500">{reinspectionSummary}</p>
                  {editable && (
                    <button type="button" className="mt-1.5 text-sm font-medium text-emerald-700 hover:underline" onClick={openReinspectEditor} data-testid="reinspection-edit">
                      Edit price &amp; wording
                    </button>
                  )}
                </div>
                {editable && (
                  <Switch checked={reinspectionOn} onCheckedChange={(v) => toggleReinspection(v)} disabled={busy} data-testid="toggle-reinspection" />
                )}
              </div>
            </Card>
          )}

          <MarketplaceQuoteCard quoteId={id} />

          {quote.notes && (
            <Card className="p-5">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Notes</h3>
                {editable && <Button size="sm" variant="ghost" onClick={() => { setNotesDraft(quote.notes || ""); setNotesOpen(true); }} data-testid="quote-edit-notes"><Pencil className="h-4 w-4" /></Button>}
              </div>
              <p className="whitespace-pre-line text-sm text-slate-600">{quote.notes}</p>
            </Card>
          )}
          {editable && !quote.notes && (
            <Button variant="outline" onClick={() => { setNotesDraft(""); setNotesOpen(true); }} data-testid="quote-add-notes"><Plus className="mr-1.5 h-4 w-4" /> Add notes</Button>
          )}
        </div>

        {/* Sidebar: totals + customer + actions */}
        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="mb-3 font-semibold text-slate-800">Summary</h3>
            <dl className="space-y-2 text-sm">
              {showLabourMaterials && (
                <>
                  <Row label="Labour subtotal" value={fmtMoney(totalLabour)} />
                  <Row label="Materials subtotal" value={fmtMoney(totalMaterials)} />
                  <div className="border-t border-slate-100" />
                </>
              )}
              <Row label="Subtotal" value={fmtMoney(quote.subtotal)} />
              {quote.discount_amount ? <Row label={quote.discount_label || "Discount"} value={`− ${fmtMoney(quote.discount_amount)}`} tone="emerald" /> : null}
              <Row label="VAT" value={fmtMoney(quote.vat)} />
              <div className="flex items-center justify-between border-t border-slate-100 pt-2">
                <dt className="font-semibold text-slate-800">Total</dt>
                <dd className="text-lg font-bold text-slate-900" data-testid="quote-total">{fmtMoney(quote.total)}</dd>
              </div>
            </dl>
            {editable && (
              <Button variant="outline" size="sm" className="mt-4 w-full" onClick={() => setDiscountOpen(true)} data-testid="quote-edit-discount">
                {quote.discount_amount ? "Edit discount" : "Add discount"}
              </Button>
            )}
          </Card>

          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Customer</h3>
              {editable && <Button size="sm" variant="ghost" onClick={() => setDetailsOpen(true)} data-testid="quote-edit-details"><Pencil className="mr-1.5 h-4 w-4" /> Edit details</Button>}
            </div>
            <div className="space-y-2 text-sm">
              <Row label="Name" value={quote.customer_name || "—"} />
              <Row label="Email" value={quote.customer_email || "—"} />
              {quote.building_id && <Row label="Building" value={<Link to={`/buildings/${quote.building_id}`} className="text-primary hover:underline">{quote.building_name || "View"}</Link>} />}
              <Row label="Payment terms" value={quote.payment_terms_days != null ? `${quote.payment_terms_days} days` : "—"} />
              <Row label="Valid until" value={fmtDate(quote.valid_until)} />
            </div>
          </Card>

          {/* Status actions */}
          <Card className="p-5">
            {quote.status === "draft" && (
              <Button className="w-full" disabled={busy} onClick={() => doSend(false)} data-testid="quote-send-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send to client
              </Button>
            )}
            {quote.status === "sent" && (
              <div className="space-y-2">
                <p className="text-sm text-slate-500">Awaiting client decision. You can record it on their behalf:</p>
                <div className="flex gap-2">
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => doDecision("accepted")} data-testid="quote-accept-btn"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Accept</Button>
                  <Button className="flex-1" variant="outline" disabled={busy} onClick={() => doDecision("declined")} data-testid="quote-decline-btn"><XCircle className="mr-1.5 h-4 w-4" /> Decline</Button>
                </div>
              </div>
            )}
            {quote.status === "accepted" && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="font-medium">Accepted</span></div>
                {invoiceList.length > 0 && (
                  <div className="space-y-1.5">
                    {invoiceList.map((inv) => (
                      <button key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)} className="flex w-full items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-left text-sm hover:bg-slate-100" data-testid={`quote-invoice-${inv.id}`}>
                        <span className="flex items-center gap-1.5"><Receipt className="h-4 w-4 text-slate-400" /> {inv.invoice_number} <span className="capitalize text-slate-400">· {inv.invoice_kind}</span></span>
                        <span className="font-semibold">{fmtMoney(inv.total)}</span>
                      </button>
                    ))}
                    <p className="text-xs text-slate-400">Invoiced so far: {fmtMoney(invoicedTotal)}</p>
                  </div>
                )}
                <Button className="w-full" disabled={busy} onClick={() => setInvoiceOpen(true)} data-testid="quote-create-invoice-btn"><Receipt className="mr-2 h-4 w-4" /> Create invoice</Button>
                {isMaintenanceQuote && (
                  <Button variant="outline" className="w-full" disabled={busy} onClick={() => setJobOpen(true)} data-testid="quote-create-job-btn"><Wrench className="mr-2 h-4 w-4" /> Assign engineer &amp; create job</Button>
                )}
              </div>
            )}
            {quote.status === "declined" && (
              <div className="flex items-center gap-2 text-red-600"><XCircle className="h-5 w-5" /><span className="font-medium">Declined{quote.decision_note ? ` — ${quote.decision_note}` : ""}</span></div>
            )}
          </Card>

          {editable && (regenerable || canReplaceDoor) && (
            <Card className="p-5" data-testid="quote-power-tools">
              <h3 className="mb-3 font-semibold text-slate-800">Power tools</h3>
              <div className="space-y-2">
                {regenerable && (
                  <Button variant="outline" className="w-full justify-start" disabled={busy} onClick={() => setRegenOpen(true)} data-testid="quote-regenerate-btn">
                    <RefreshCw className="mr-2 h-4 w-4" /> Regenerate from inspection
                  </Button>
                )}
                {canReplaceDoor && (
                  <Button variant="outline" className="w-full justify-start" disabled={busy} onClick={() => setReplaceOpen(true)} data-testid="quote-replace-door-btn">
                    <Repeat className="mr-2 h-4 w-4" /> Replace with new door
                  </Button>
                )}
              </div>
              <p className="mt-3 text-xs text-slate-400">Regenerate rebuilds line items from the linked inspection at current prices. Replace swaps a door&apos;s failed items for a compliant new-door bundle.</p>
            </Card>
          )}
        </div>
      </div>

      <LineItemDialog open={lineDialog.open} onOpenChange={(o) => setLineDialog((s) => ({ ...s, open: o }))} initial={lineDialog.initial} onSave={addOrEditLine} saving={busy} />
      <DiscountDialog open={discountOpen} onOpenChange={setDiscountOpen} initial={{ amount: quote.discount_amount, label: quote.discount_label }} onSave={saveDiscount} saving={busy} />
      <ReplaceDoorDialog open={replaceOpen} onOpenChange={setReplaceOpen} doors={replaceableDoors} preselect={replaceableDoors.length === 1 ? replaceableDoors[0].id : null} onConfirm={doConvertDoor} saving={busy} />
      <DetailsDialog open={detailsOpen} onOpenChange={setDetailsOpen} initial={quote} onSave={saveDetails} saving={busy} />
      <CustomisePdfDialog open={customiseOpen} onOpenChange={setCustomiseOpen} quoteId={id} company={companyTpl} onError={(m) => toast.error(m)} />

      <Dialog open={reinspectOpen} onOpenChange={setReinspectOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Post-work inspection line</DialogTitle>
            <DialogDescription>Sets the per-door price and wording for the compliance report line. Applies to every door on the quote.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Price per door (£)</Label>
              <Input type="number" step="0.01" min="0" value={reinspectPrice} onChange={(e) => setReinspectPrice(e.target.value)} data-testid="reinspection-price-input" />
            </div>
            <div className="space-y-2">
              <Label>Wording shown to client</Label>
              <Textarea rows={3} value={reinspectWording} onChange={(e) => setReinspectWording(e.target.value)} data-testid="reinspection-wording-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveReinspectEditor} disabled={busy} data-testid="reinspection-save-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Regenerate from inspection</DialogTitle>
            <DialogDescription>Rebuild this quote&apos;s line items from its linked inspection using the latest pricing. Any manual edits to the line items will be replaced.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenOpen(false)} disabled={busy} data-testid="quote-regenerate-cancel">Cancel</Button>
            <Button onClick={doRegenerate} disabled={busy} data-testid="quote-regenerate-confirm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Quote notes</DialogTitle><DialogDescription>Shown to the client on the quote.</DialogDescription></DialogHeader>
          <Textarea rows={5} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} data-testid="quote-notes-input" />
          <DialogFooter><Button onClick={saveNotes} disabled={busy} data-testid="quote-notes-save">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create invoice</DialogTitle><DialogDescription>Choose how much of the accepted quote to bill now.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {DEPOSIT_OPTS.map((o) => (
              <Button key={o.label} variant="outline" className="w-full justify-start" disabled={busy} onClick={() => createInvoice(o.body)} data-testid={`invoice-opt-${o.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <Receipt className="mr-2 h-4 w-4" /> {o.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={jobOpen} onOpenChange={setJobOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create maintenance job</DialogTitle><DialogDescription>Assign an engineer to carry out this accepted work. Multi-door quotes split into one job per door.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Label>Engineer</Label>
            <Select value={jobEngineer || "none"} onValueChange={(v) => setJobEngineer(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="job-engineer-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Assign later</SelectItem>
                {engList.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button onClick={createJob} disabled={busy} data-testid="job-create-confirm"><Wrench className="mr-2 h-4 w-4" /> Create job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/quotes" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
      <ArrowLeft className="h-4 w-4" /> Quotes
    </Link>
  );
}

function Row({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`font-medium ${tone === "emerald" ? "text-emerald-600" : "text-slate-800"}`}>{value}</dd>
    </div>
  );
}
